import { createInformationApi, updateInformationApi } from "@/api/services/admin/information";
import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/components/elements/Button";
import { Editor } from "@/components/elements/Editor";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { Switch } from "@/components/elements/Switch";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { FILE_UPLOAD_FOR, MIME_TYPES } from "@/constants/file";
import { informationSchema } from "@/features/information/schema/informationSchema";
import { TInformationForm } from "@/features/information/types/informationForm";
import { convertInformationFormToCreateReq, convertInformationFormToUpdateReq } from "@/features/information/utils/convert";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { TExtendFile } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/system";
import { PreviewInformationModal } from "@/features/information/components/PreviewInformationModal";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { UploadFile } from "@/features/information/components/UploadFile";
import { FileItem } from "@/components/elements/FileItem";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { convertKbToByte } from "@/utils/file";
import { TopPage } from "@/components/elements/TopPage";

type TInformationFormProps = {
  previousData?: Partial<TInformationForm>;
  backUrl?: string;
};

const initForm: TInformationForm = {
  title: "",
  content: "",
  enableEndDate: false,
  enableStartDate: false,
  endDate: null,
  filePath: null,
  startDate: null,
  file: null,
  type: "",
};

export const InformationForm: FC<TInformationFormProps> = ({ previousData, backUrl }) => {
  const { id } = useParams();
  const { data: informationTypes } = useEnum("EInformationType");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const methods = useForm<TInformationForm>({
    defaultValues: previousData ?? initForm,
    resolver: yupResolver(informationSchema),
    mode: "onBlur",
  });
  const {
    control,
    clearErrors,
    getValues,
    setValue,
    formState: { errors },
  } = methods;
  const { enableStartDate, enableEndDate } = useWatch({ control });
  const startDateWatch = useWatch({ control, name: "startDate" });
  const endDateWatch = useWatch({ control, name: "endDate" });
  const [openPreview, setOpenPreview] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState<File | null>(null);
  const filePath = useWatch({ control, name: "filePath" });
  const [informationStatus, setInformationStatus] = useState("下書き");
  const [isSubmit, setIsSubmit] = useState(false);
  const acceptMimeTypes = [
    MIME_TYPES.PDF,
    MIME_TYPES.WORD,
    MIME_TYPES.EXCEL,
    MIME_TYPES.TXT,
    MIME_TYPES.IMAGE,
    MIME_TYPES.CSV,
    MIME_TYPES.STACK,
    MIME_TYPES.PPT,
  ].flat();

  useEffect(() => {
    if (!enableStartDate) {
      clearErrors("startDate");
      setValue("startDate", dayjs());
    }
    if (!enableEndDate) {
      clearErrors("endDate");
      setValue("endDate", dayjs());
    }
    const status = getStatus();
    setInformationStatus(status);
  }, [enableStartDate, enableEndDate]);

  useEffect(() => {
    if (errors.endDate?.type === "isSameOrAfterDate") {
      clearErrors("endDate");
    }
  }, [startDateWatch]);

  useEffect(() => {
    if (errors.startDate?.type === "isSameOrBeforeDate") {
      clearErrors("startDate");
    }
  }, [endDateWatch]);

  const onSubmit = async (data: TInformationForm) => {
    await handleSaveData(data, false);
  };

  const handleSaveDraft = async () => {
    try {
      const payload = getValues();
      await handleSaveData(payload, true);
      methods.reset();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleSaveData = async (data: TInformationForm, isDraft: boolean) => {
    setIsSubmit(true);
    try {
      if (id) {
        const payload = convertInformationFormToUpdateReq(data, isDraft);
        payload.type = payload.type || null;
        payload.startDate = payload.startDate ? dayjs(payload.startDate).format("YYYY-MM-DD") : null;
        payload.endDate = payload.endDate ? dayjs(payload.endDate).format("YYYY-MM-DD") : null;
        payload.file = fileUploaded as TExtendFile;
        const rs = await updateInformationApi(id, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_DETAIL, id.toString()] });
      } else {
        const payload = convertInformationFormToCreateReq(data, isDraft);
        payload.type = payload.type || null;
        payload.file = fileUploaded as TExtendFile;
        const rs = await createInformationApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_LIST] });
      navigate(backUrl || AppRoutes.information);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const onFinishUpload = (file: TExtendFile[]) => {
    const fileChosen = file[0] as File;
    setFileUploaded(fileChosen);
    setValue("file", file);
  };

  const removeFile = () => {
    setFileUploaded(null);
    methods.setValue("filePath", null);
  };

  const getStatus = () => {
    if (!id) {
      return "下書き";
    }
    const informationData = getValues();
    const { type, isDraft } = informationData;
    if (!type) {
      return "下書き";
    }
    if (isDraft) {
      return "下書き";
    }
    if (!informationData.startDate) {
      return "公開済み";
    }
    const { emailBroadCastHistories } = informationData;

    if (emailBroadCastHistories && emailBroadCastHistories.length > 0) {
      return "公開済み";
    }

    const isSameOrBefore = dayjs(informationData.startDate).isSameOrBefore(dayjs(), "day");
    if (isSameOrBefore) {
      return "公開済み";
    }
    return "未公開";
  };
  return (
    <Form methods={methods} onSubmit={onSubmit} shouldBlock={uploading} schema={informationSchema}>
      <TopPage
        title={previousData ? "お知らせ編集" : "新しいお知らせを作成する"}
        description={id ? `ステータス：${informationStatus}` : ""}
        enablePageBack={false}
      />

      <Stack width="100%" mt={5}>
        <SectionContainer>
          <Stack gap={2} className={"border"}>
            <Typography variant="sub16Bold">公開編集</Typography>
            <FormField control={control} name="type">
              <RadioGroup
                options={informationTypes.map((option, index) => {
                  if (index === 0) return { ...option, label: `${option.label}（トップページ上部）` };
                  return option;
                })}
                direction="column"
              />
            </FormField>
            <Stack gap={0.5}>
              <Typography variant="cap12Bold">公開開始日</Typography>
              <Typography variant="cap12">メンテナンス時間に合わせて表示する場合などに設定</Typography>
              <Stack direction="row" alignItems="center" gap={3}>
                <Typography variant="cap12" whiteSpace="nowrap">
                  設定
                </Typography>
                <FormField control={control} name="enableStartDate">
                  <Switch />
                </FormField>
              </Stack>
              <FormField control={control} name="startDate" sx={{ maxWidth: 250 }} label="">
                <InputDatePicker format="YYYY/MM/DD" disabled={!enableStartDate} />
              </FormField>
            </Stack>
            <Stack gap={0.5}>
              <Typography variant="cap12Bold">公開終了日</Typography>
              <Stack direction="row" alignItems="center" gap={3}>
                <Typography variant="cap12" whiteSpace="nowrap">
                  設定
                </Typography>
                <FormField control={control} name="enableEndDate">
                  <Switch />
                </FormField>
              </Stack>
              <FormField control={control} name="endDate" sx={{ maxWidth: 250 }} label="">
                <InputDatePicker format="YYYY/MM/DD" disabled={!enableEndDate} minDate={!previousData ? dayjs() : undefined} />
              </FormField>
            </Stack>
          </Stack>
        </SectionContainer>
        <SectionContainer>
          <Stack gap={2}>
            <Typography variant="sub16Bold">お知らせ内容</Typography>
            <FormField control={control} name="title">
              <Input size="lg" />
            </FormField>
            <FormField control={control} name="content">
              <Editor />
            </FormField>
          </Stack>
        </SectionContainer>
        <SectionContainer>
          <Stack gap={1}>
            <Typography variant="sub16Bold">ファイル添付</Typography>
            <Typography variant="cap12">1ファイルのみ添付可能です</Typography>
            <Button size="lg" variant="outline" disabled={uploading} onClick={() => setOpenUpload(true)}>
              ファイルを添付
            </Button>
            {fileUploaded && <FileItem file={fileUploaded} onRemove={removeFile} downloadable width={200} />}
            {filePath && !fileUploaded && <FileItem filePath={filePath} onRemove={removeFile} downloadable width={200} />}
          </Stack>
        </SectionContainer>
        <UploadFile
          isOpen={openUpload}
          onClose={() => setOpenUpload(false)}
          onCancel={() => setUploading(false)}
          uploadFor={FILE_UPLOAD_FOR.INFORMATION_FILE}
          rules={{ maxSize: convertKbToByte(10 * 1000), acceptMimeTypes: acceptMimeTypes }}
          options={{ multiple: false }}
          onFinishUpload={onFinishUpload}
          title={<Typography variant="body14Bold">ファイルをアップロードしてください。</Typography>}
        >
          <Stack alignItems="center" justifyContent="center" gap={2}>
            <Typography variant="body14" whiteSpace="pre-line" textAlign="center">{`ファイルを\nドラッグ&ドロップ`}</Typography>
            <Typography variant="body14">１ファイルのみ添付可能です</Typography>
          </Stack>
        </UploadFile>
        {openPreview && <PreviewInformationModal file={fileUploaded} open={openPreview} onClose={() => setOpenPreview(false)} />}
        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Button size="sm-round" variant="secondary" onClick={() => navigate(backUrl || AppRoutes.information)}>
              戻る
            </Button>
            <Stack direction="row" gap={3}>
              <Button variant="outline" size="sm-round-vertical" onClick={() => setOpenPreview(true)}>
                プレビュー
              </Button>
              <Button disabled={isSubmit} variant="outline" size="sm-round-vertical" onClick={handleSaveDraft}>
                下書き保存
              </Button>
              <Button disabled={isSubmit} size="sm-round" type="submit">
                更新
              </Button>
            </Stack>
          </Stack>
        </FooterContainer>
      </Stack>
    </Form>
  );
};

const SectionContainer = styled(Stack)`
  padding: 24px;
  border: 1px solid #e6e7e8;
  border-radius: 8px;
  background-color: #ffffff;
  margin-bottom: 32px;
`;
